import { Directive, ElementRef, Host, Input, OnInit, Optional, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatButtonToggle } from '@angular/material/button-toggle';
import { MatLegacyButton } from '@angular/material/legacy-button';
import { MatLegacyMenuItem } from '@angular/material/legacy-menu';
import { MatMenuItem } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ButtonComponent } from '@y42/core/button';
import { DisableControlDirective } from '@y42/core/disable-control';
import { NumberInputComponent, TextareaInputComponent, TextInputComponent, _InputComponent } from '@y42/core/input';
import { isNotNullish } from '@y42/util/nullish';
import { BehaviorSubject, combineLatest, filter, of, shareReplay, tap, withLatestFrom } from 'rxjs';
import { AclPermissionDetail } from '../../models';

@UntilDestroy()
@Directive({
  selector: '[y42Permission]',
  exportAs: 'permission',
})
export class AclPermissionDirective implements OnInit {
  constructor(
    private readonly elementRef: ElementRef,
    @Optional() private readonly ngControl: NgControl | null,
    @Optional() private readonly buttonHost: ButtonComponent | null,
    @Optional() private readonly matLegacyButtonHost: MatLegacyButton | null,
    @Optional() private readonly matButtonHost: MatButton | null,
    @Optional() private readonly matButtonToggleHost: MatButtonToggle | null,
    @Optional() private readonly matLegacyMenuItemHost: MatLegacyMenuItem | null,
    @Optional() private readonly matMenuItemHost: MatMenuItem | null,
    @Optional() private readonly textInputComponent: TextInputComponent | null,
    @Optional() private readonly numberInputComponent: NumberInputComponent | null,
    @Optional() private readonly textareaInputComponent: TextareaInputComponent | null,
    // Will inject the matTooltip from the host element or nearest DOM parent
    // It needs to be injected this way because disabled elements don't show a matTooltip because
    // the hover effect is disabled and therefore the matTooltip needs to be added to a div wrapper
    @Optional() private readonly matTooltip: MatTooltip | null,
    @Optional() @Host() private readonly disableControlDirective: DisableControlDirective | null,
    private readonly renderer: Renderer2,
  ) {}

  private readonly permissionDetail$ = new BehaviorSubject<AclPermissionDetail | null>(null);
  private readonly permissionTooltip$ = new BehaviorSubject('');

  @Input('y42Permission') set permissionDetail(permissionDetail: AclPermissionDetail) {
    this.permissionDetail$.next(permissionDetail);
  }
  @Input() set permissionTooltip(tooltip: string) {
    this.permissionTooltip$.next(tooltip);
  }

  readonly permission$ = this.permissionDetail$.pipe(
    filter(isNotNullish),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  ngOnInit() {
    combineLatest([this.disableControlDirective?.disabled$ || of(false), this.permission$])
      .pipe(
        withLatestFrom(this.permissionTooltip$),
        tap(([[disabled, permissionDetail], tooltip]) => {
          this.toggleEnable(permissionDetail, tooltip, disabled);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private toggleEnable(permissionDetail: AclPermissionDetail, tooltip: string, disabled: boolean): void {
    const enabled = permissionDetail.allowed && !disabled;
    if (this.ngControl) {
      this.ngControl.control?.[enabled ? 'enable' : 'disable']();
    } else if (this.textInputComponent) {
      this.textInputComponent.formCtrl[enabled ? 'enable' : 'disable']();
    } else if (this.numberInputComponent) {
      this.numberInputComponent.formCtrl[enabled ? 'enable' : 'disable']();
    } else if (this.textareaInputComponent) {
      this.textareaInputComponent.formCtrl[enabled ? 'enable' : 'disable']();
    } else if (this.buttonHost) {
      this.buttonHost.disabled = !enabled;
    } else if (this.matButtonToggleHost) {
      this.matButtonToggleHost.disabled = !enabled;
    } else if (this.matLegacyMenuItemHost) {
      this.matLegacyMenuItemHost.disabled = !enabled;
    } else if (this.matMenuItemHost) {
      this.matMenuItemHost.disabled = !enabled;
    } else if (this.matLegacyButtonHost) {
      this.matLegacyButtonHost.disabled = !enabled;
    } else if (this.matButtonHost) {
      this.matButtonHost.disabled = !enabled;
    } else if (enabled) {
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'disabled');
    } else {
      this.renderer.setAttribute(this.elementRef.nativeElement, 'disabled', '');
    }

    if (permissionDetail.allowed) {
      this.setTooltip('');
      return;
    }

    this.setTooltip(permissionDetail.detail || tooltip);
  }

  private setTooltip(message: string): void {
    const inputComponent = this.getInputComponent();
    if (inputComponent) {
      inputComponent.setAclTooltip(message);
    } else if (this.matTooltip) {
      this.matTooltip.message = message;
    }
  }

  private getInputComponent(): _InputComponent | null {
    return this.textInputComponent || this.numberInputComponent || this.textareaInputComponent;
  }
}
